import React, {FormEvent, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {useQuery} from "@apollo/client"
import {useHistory, useLocation} from "react-router-dom"

import searchQuery from "graphql/Media/Search/query/search"
import {getImageUrlFromSizeAndPath, TmdbImageSizeEnum} from "helpers/Tmdb"
import {
  URL_FRONT_COLLECTION_PLAY,
  URL_FRONT_EPISODE_PLAY,
  URL_FRONT_MOVIE_PLAY,
  URL_FRONT_SHOW_PLAY,
} from "static/constant/url"

import "./style.scss"

type SearchFilter = "ALL" | "Movie" | "Show" | "Collection" | "Episode"

const SEARCH_TEXT_MIN_LENGTH = 3
const SEARCH_DELAY = 350

const filters: {value: SearchFilter, label: string, icon: string}[] = [
  {value: "ALL", label: "Tout", icon: "fa-border-all"},
  {value: "Movie", label: "Films", icon: "fa-film"},
  {value: "Show", label: "Séries", icon: "fa-tv"},
  {value: "Collection", label: "Collections", icon: "fa-layer-group"},
  {value: "Episode", label: "Épisodes", icon: "fa-play-circle"},
]

const resultLabels: Record<string, string> = {
  Movie: "Film",
  Show: "Série",
  Collection: "Collection",
  Episode: "Épisode",
}

const getResultPath = (result: any) => {
  switch (result?.__typename) {
    case "Movie":
      return result.isViewable ? URL_FRONT_MOVIE_PLAY.replace(":id", result.id) : null
    case "Show":
      return URL_FRONT_SHOW_PLAY.replace(":id", result.id)
    case "Collection":
      return URL_FRONT_COLLECTION_PLAY.replace(":id", result.id)
    case "Episode":
      return result.isViewable ? URL_FRONT_EPISODE_PLAY.replace(":id", result.id) : null
    default:
      return null
  }
}

const getResultImage = (result: any) => {
  const path = result?.__typename === "Episode"
    ? result?.tmdbOptions?.still_path
    : result?.tmdbOptions?.poster_path
  return path ? getImageUrlFromSizeAndPath(TmdbImageSizeEnum.W342, path) : ""
}

const getResultDetails = (result: any) => {
  if (result?.__typename === "Episode") {
    return `${result?.Season?.Show?.name || "Série"} · S${result?.Season?.number || "–"} E${result?.number || "–"}`
  }

  const date = result?.tmdbOptions?.release_date || result?.tmdbOptions?.first_air_date
  const year = date ? String(date).slice(0, 4) : ""
  return [resultLabels[result?.__typename], year].filter(Boolean).join(" · ")
}

const SearchResultCard = ({result}: {result: any}) => {
  const history = useHistory()
  const destination = getResultPath(result)
  const imageUrl = getResultImage(result)
  const isEpisode = result?.__typename === "Episode"

  return <button
    type="button"
    className={`search-result-card result-${String(result?.__typename).toLowerCase()} ${!destination ? "is-unavailable" : ""}`}
    onClick={() => destination && history.push(destination)}
    disabled={!destination}
    aria-label={destination ? `Ouvrir ${result?.name}` : `${result?.name}, bientôt disponible`}
  >
    <span
      className={`search-result-visual ${imageUrl ? "has-image" : "no-image"}`}
      style={imageUrl ? {backgroundImage: `url(${imageUrl})`} : undefined}
    >
      {!imageUrl && <span className={`fas ${isEpisode ? "fa-photo-video" : "fa-film"}`} aria-hidden="true"></span>}
      <span className="result-visual-shade"></span>
      <span className="result-type-badge">{resultLabels[result?.__typename] || "Média"}</span>
      <span className="result-play-control" aria-hidden="true">
        <span className={`fas ${destination ? "fa-play" : "fa-clock"}`}></span>
      </span>
    </span>
    <span className="search-result-copy">
      <strong>{result?.name || "Sans titre"}</strong>
      <small>{getResultDetails(result)}</small>
      {!destination && <span className="result-availability">Bientôt disponible</span>}
    </span>
  </button>
}

export const Search = () => {
  const history = useHistory()
  const location = useLocation()
  const initialQuery = new URLSearchParams(location.search).get("q") || ""
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestIdRef = useRef(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [text, setText] = useState(initialQuery)
  const [submittedText, setSubmittedText] = useState("")
  const [activeFilter, setActiveFilter] = useState<SearchFilter>("ALL")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [resultList, setResultList] = useState<any[] | null>(null)
  const {refetch} = useQuery(searchQuery, {skip: true, fetchPolicy: "no-cache"})

  const runSearch = useCallback(async (rawValue: string) => {
    const value = rawValue.trim()
    if (value.length < SEARCH_TEXT_MIN_LENGTH) return

    const requestId = ++requestIdRef.current
    setLoading(true)
    setError("")
    setSubmittedText(value)
    setActiveFilter("ALL")

    try {
      const response = await refetch({text: value})
      if (requestId !== requestIdRef.current) return
      setResultList(response?.data?.search || [])
      history.replace({pathname: location.pathname, search: `?q=${encodeURIComponent(value)}`})
    } catch (exception) {
      if (requestId !== requestIdRef.current) return
      setError("La recherche n'est pas disponible pour le moment.")
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [history, location.pathname, refetch])

  useEffect(() => {
    const value = text.trim()
    if (timeoutRef.current) clearTimeout(timeoutRef.current)

    if (value.length < SEARCH_TEXT_MIN_LENGTH) {
      requestIdRef.current += 1
      setLoading(false)
      setError("")
      setResultList(null)
      setSubmittedText("")
      setActiveFilter("ALL")
      history.replace({pathname: location.pathname, search: ""})
      return
    }

    timeoutRef.current = setTimeout(() => runSearch(value), SEARCH_DELAY)
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [history, location.pathname, runSearch, text])

  const counts = useMemo(() => {
    const values: Record<SearchFilter, number> = {ALL: resultList?.length || 0, Movie: 0, Show: 0, Collection: 0, Episode: 0}
    resultList?.forEach(result => {
      if (result?.__typename in values) values[result.__typename as SearchFilter] += 1
    })
    return values
  }, [resultList])

  const visibleResults = useMemo(() => activeFilter === "ALL"
    ? resultList || []
    : (resultList || []).filter(result => result?.__typename === activeFilter), [activeFilter, resultList])

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    runSearch(text)
  }

  const clearSearch = () => {
    setText("")
    inputRef.current?.focus()
  }

  const canSearch = text.trim().length >= SEARCH_TEXT_MIN_LENGTH
  const hasSearched = resultList !== null
  const hasNoResults = hasSearched && resultList.length === 0 && !loading

  return <main className="search-container">
    <header className="search-hero">
      <span className="search-eyebrow">Explorer la bibliothèque</span>
      <h1>Que voulez-vous regarder&nbsp;?</h1>
      <p>Films, séries, collections et épisodes — toute votre vidéothèque en une recherche.</p>

      <form className={`search-form ${loading ? "is-loading" : ""}`} onSubmit={onSubmit} role="search">
        <span className="search-field-icon fas fa-search" aria-hidden="true"></span>
        <label className="sr-only" htmlFor="zipflix-search">Rechercher dans Zipflix</label>
        <input
          ref={inputRef}
          id="zipflix-search"
          type="search"
          autoComplete="off"
          spellCheck={false}
          placeholder="Un titre, une série, un épisode…"
          value={text}
          onChange={event => setText(event.target.value)}
        />
        {text && <button type="button" className="search-clear-button" onClick={clearSearch} aria-label="Effacer la recherche"><span className="fas fa-times"></span></button>}
        <button className="search-submit-button" type="submit" disabled={!canSearch || loading}>
          {loading ? <span className="icon-spinner" aria-hidden="true"></span> : <span className="fas fa-arrow-right" aria-hidden="true"></span>}
          <span>{loading ? "Recherche…" : "Rechercher"}</span>
        </button>
      </form>
      <div className="search-help-line">
        <span>{text.trim().length > 0 && text.trim().length < SEARCH_TEXT_MIN_LENGTH ? `Encore ${SEARCH_TEXT_MIN_LENGTH - text.trim().length} caractère${SEARCH_TEXT_MIN_LENGTH - text.trim().length > 1 ? "s" : ""}` : "Les résultats apparaissent pendant la saisie"}</span>
        <span className="search-key-hint"><kbd>Entrée</kbd> pour lancer</span>
      </div>
    </header>

    {!hasSearched && !loading && <section className="search-discovery-state">
      <span className="discovery-icon fas fa-compass" aria-hidden="true"></span>
      <h2>Votre prochaine séance commence ici</h2>
      <p>Saisissez au moins trois caractères pour parcourir l'ensemble du catalogue.</p>
      <div className="discovery-types">
        {filters.slice(1).map(filter => <span key={filter.value}><i className={`fas ${filter.icon}`}></i>{filter.label}</span>)}
      </div>
    </section>}

    {loading && !hasSearched && <section className="search-loading-grid" aria-live="polite" aria-label="Recherche en cours">
      {[0, 1, 2, 3, 4].map(item => <div className="search-skeleton" key={item}><span></span><i></i><i></i></div>)}
    </section>}

    {error && <section className="search-message-state search-error-state" role="alert">
      <span className="fas fa-plug" aria-hidden="true"></span>
      <h2>Recherche indisponible</h2>
      <p>{error}</p>
      <button type="button" onClick={() => runSearch(text)}><span className="fas fa-redo"></span>Réessayer</button>
    </section>}

    {hasNoResults && !error && <section className="search-message-state">
      <span className="fas fa-search-minus" aria-hidden="true"></span>
      <h2>Aucun résultat pour «&nbsp;{submittedText}&nbsp;»</h2>
      <p>Essayez un titre plus court, un autre mot-clé ou vérifiez l'orthographe.</p>
      <button type="button" onClick={clearSearch}>Modifier la recherche</button>
    </section>}

    {hasSearched && resultList.length > 0 && !error && <section className="search-results" aria-live="polite">
      <header className="search-results-header">
        <div>
          <span className="search-eyebrow">Résultats</span>
          <h2>{counts.ALL} résultat{counts.ALL > 1 ? "s" : ""} pour «&nbsp;{submittedText}&nbsp;»</h2>
        </div>
        {loading && <span className="search-refresh-status"><span className="icon-spinner"></span>Actualisation</span>}
      </header>

      <div className="search-filters" role="group" aria-label="Filtrer les résultats">
        {filters.map(filter => <button
          type="button"
          className={activeFilter === filter.value ? "is-active" : ""}
          onClick={() => setActiveFilter(filter.value)}
          disabled={filter.value !== "ALL" && counts[filter.value] === 0}
          aria-pressed={activeFilter === filter.value}
          key={filter.value}
        >
          <span className={`fas ${filter.icon}`} aria-hidden="true"></span>
          {filter.label}
          <small>{counts[filter.value]}</small>
        </button>)}
      </div>

      <div className="search-result-grid">
        {visibleResults.map(result => <SearchResultCard result={result} key={`${result.__typename}-${result.id}`} />)}
      </div>
    </section>}
  </main>
}
